import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE } from './api.config';
import { SessionService } from '../../../core/services/session.service';

export interface ApiList<T> {
  data: T[];
  meta?: {
    count?: number;
    total?: number;
    page?: number;
    pageSize?: number;
    totalPages?: number;
    authorityId?: number;
    authorityCode?: string;
    schema?: string;
  };
}

export interface ApiOne<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export interface ByeLawNorm {
  id: number;
  tenantId?: number;
  useTypeId?: number;
  useTypeCode?: string;
  useTypeName?: string;
  categoryCode?: string;
  categoryName?: string;
  clauseRef?: string;
  plotAreaMin?: string | number | null;
  plotAreaMax?: string | number | null;
  roadWidthMin?: string | number | null;
  roadWidthMax?: string | number | null;
  groundCoveragePct?: string | number | null;
  farBase?: string | number | null;
  farMax?: string | number | null;
  heightMax?: string | number | null;
  setbackFront?: string | number | null;
  setbackRear?: string | number | null;
  setbackSide1?: string | number | null;
  setbackSide2?: string | number | null;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  attributes?: string | Record<string, unknown> | null;
  remarks?: string | null;
  statusCd: string;
}

export interface HierarchyType {
  id: number;
  tenantId?: number | null;
  module: string;
  parentId?: number | null;
  parentCode?: string | null;
  parentName?: string | null;
  code: string;
  name: string;
  level: string;
  sortOrder: number;
  isActive: boolean | number;
  statusCd: string;
}

export interface SystemCodeValue {
  id: number;
  tenantId?: number | null;
  module: string;
  codeType: string;
  code: string;
  label: string;
  description?: string | null;
  sortOrder: number;
  isActive: boolean | number;
  statusCd: string;
}

export interface PilotArea {
  id: number;
  authorityId: number;
  name: string;
  boundaryRef?: string | null;
  notes?: string | null;
  statusCd: string;
}

/** LARIS location hierarchy — Mam spec (location_types.fields) */
export interface LocationTypeField {
  key: string;
  label: string;
  field_type: string;
  is_required: boolean;
}

export interface LocationType {
  id: number;
  tenantId: number | null;
  code: string;
  name: string;
  fields: LocationTypeField[];
  isActive: boolean;
  statusCd: string;
}

export interface SystemLocation {
  id: number;
  tenantId: number;
  locationTypeId: number;
  locationTypeCode: string;
  locationTypeName: string;
  parentLocationId: number | null;
  parentCode?: string | null;
  parentName?: string | null;
  code: string;
  name: string;
  boundaryGeojson?: Record<string, unknown> | null;
  attributes: Record<string, unknown>;
  fields?: LocationTypeField[];
  isActive: boolean;
  statusCd: string;
  children?: SystemLocation[];
  lidarScans?: LidarScan[];
}

export interface LidarScan {
  id: number;
  tenantId?: number;
  locationId: number;
  documentId?: number | null;
  scanDate: string;
  pointCloudFormat: string;
  status: string;
  pointCount?: number | null;
  scannedExtentGeojson?: Record<string, unknown> | null;
  attributes?: Record<string, unknown>;
  statusCd?: string;
}

export interface AreaScanSummary {
  mode?: string;
  registeredPlotCount?: number;
  buildingsDetected?: number;
  matchedCount?: number;
  unmatchedBuildings?: Array<{ detectionIndex?: number; reason?: string }>;
  unmatchedPlots?: Array<{ plotRef?: string; reason?: string }>;
  missingBoundaries?: string[];
  statusCd?: string;
  note?: string;
  processedAt?: string;
}

export interface LocationScanProcessingRun {
  id: number;
  locationId?: number;
  statusCd?: string;
  startedAt?: string;
  endedAt?: string;
  areaScanSummary?: AreaScanSummary | null;
}

export interface SpatialNode {
  id: number;
  parentId: number | null;
  nodeType: string;
  /** @deprecated use nodeType — kept for cached localStorage entries */
  level?: string;
  code: string;
  name: string;
  authorityCode: string;
  statusCd: string;
  hasBoundary: boolean;
  isPlotRegistry?: boolean;
}

export interface SpatialNodeType {
  id: number;
  typeName: string;
  sortOrder: number;
  statusCd: string;
}

export interface SpatialBoundary {
  id: number;
  parentId?: number | null;
  nodeType: string;
  code: string;
  name: string;
  authorityCode: string;
  crsEpsg: number;
  boundaryGeojson: Record<string, unknown>;
}

export interface PlotBoundary {
  id: number;
  plotId: string;
  spatialNodeId: number;
  areaSqm: number | string | null;
  crsEpsg: number;
  statusCd: string;
  nodeCode?: string;
  nodeName?: string;
  nodeType?: string;
  nodeLevel?: string;
  boundaryGeojson: Record<string, unknown> | null;
}

export interface BulkPlotBoundaryImportResult {
  matches?: BulkPlotBoundaryMatch[];
  saved?: BulkPlotBoundarySavedRow[];
  unmatchedFeatures?: BulkPlotBoundaryUnmatchedFeature[];
  unmatchedPlots?: BulkPlotBoundaryUnmatchedPlot[];
  duplicatePlotIds?: number[];
}

export interface BulkPlotBoundaryMatch {
  featureIndex: number;
  locationId: number;
  plotCode: string;
  plotName: string;
  matchKey: string;
  boundaryGeojson?: Record<string, unknown>;
}

export interface BulkPlotBoundarySavedRow {
  locationId: number;
  code: string;
  name: string;
  matchKey: string;
}

export interface BulkPlotBoundaryUnmatchedFeature {
  index: number;
  matchKeys: string[];
  properties: Record<string, unknown>;
}

export interface BulkPlotBoundaryUnmatchedPlot {
  id: number;
  code: string;
  name: string;
}

export interface ScanBatch {
  id: number;
  authorityId: number;
  spatialNodeId: number;
  systemLocationId?: number | null;
  spatialNodeCode: string;
  spatialLevel: string;
  batchLabel?: string | null;
  expectedFileCount: number;
  registeredFileCount: number;
  notes?: string | null;
  statusCd: string;
  metadataJson?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SanctionedPlan {
  id: number;
  plotRef: string;
  planNo: string;
  statusCd: string;
  sourceType?: string;
  originalStorageUri?: string;
  cleanedStorageUri?: string | null;
  ocrConfidence?: string | number | null;
  dimensionsJson?: Record<string, unknown> | null;
  verifiedBy?: string | null;
  verifiedAt?: string | null;
  txnStart?: string | null;
}

export interface ScanJob {
  id: number;
  pilotAreaId?: number | null;
  locationId?: number | null;
  scanType: string;
  rawStorageUri: string;
  fileFormat?: string;
  operatorName?: string | null;
  statusCd: string;
  createdAt?: string;
}

export interface LidarUploadRow {
  job: ScanJob;
  locationId: number | null;
  locationName: string;
  locationCode: string;
  locationTypeCode: string;
}

export interface DeleteImpact {
  hasDerivedData: boolean;
  measurementCount: number;
  flagCount: number;
  processingRunCount?: number;
  statusCd?: string;
}

export interface Measurement {
  id: number;
  pilotAreaId?: number | null;
  locationId?: number | null;
  plotRef: string;
  setbackFrontM?: string | number;
  heightM?: string | number;
  farValue?: string | number;
  groundCoverage?: string | number;
  plotAreaSqm?: string | number;
  measuredAt?: string;
  statusCd: string;
}

export interface ViolationFlag {
  id: number;
  plotRef: string;
  summary: string;
  statusCd: string;
  referenceMode?: string;
  confidenceScore?: string | number;
  violatedRulesJson?: ViolationRule[];
  measurementId?: number;
  sanctionedPlanId?: number;
}

export interface ViolationRule {
  ruleCode: string;
  ruleName?: string;
  measured: number;
  limit: number;
  tolerance?: number;
  unit?: string;
  source?: string;
}

export interface FlagDetail {
  flag: ViolationFlag;
  measurement: (Record<string, unknown> & { footprintGeojson?: unknown }) | null;
  plan: (Record<string, unknown> & { footprintGeojson?: unknown }) | null;
  reviews: Array<{
    id: number;
    decision: string;
    comment?: string;
    reviewerUid: string;
    decidedAt?: string;
  }>;
}

export interface RuleCheckRuleResult {
  ruleCode: string;
  ruleName?: string;
  status: 'pass' | 'fail' | 'skipped' | string;
  measured?: number;
  limit?: number;
  tolerance?: number;
  unit?: string;
  source?: string;
  reason?: string;
}

export interface RuleCheckPlotResult {
  plotRef: string | null;
  measurementId?: number;
  referenceMode?: string | null;
  compliant: boolean;
  flagId?: number | null;
  violationCount?: number;
  rules?: RuleCheckRuleResult[];
  summaryOnly?: boolean;
  buildingsChecked?: number;
}

export interface RuleCheckRun {
  id: number;
  pilotAreaId: number;
  runLabel?: string | null;
  buildingsChecked: number;
  flagsRaised: number;
  statusCd: string;
  startedAt?: string | null;
  endedAt?: string | null;
  plotResults?: RuleCheckPlotResult[];
}

@Injectable({ providedIn: 'root' })
export class SbcApiService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);

  private opts() {
    return { headers: new HttpHeaders(this.session.headers()) };
  }

  private uploadOpts() {
    return { headers: new HttpHeaders(this.session.headers({ json: false })) };
  }

  /** DB-backed login — no session headers required. */
  login(body: {
    userCode: string;
    authorityCode: string;
    password: string;
  }): Observable<
    ApiOne<{
      id: number;
      userId: string;
      userCode: string;
      displayName: string;
      role: string;
      authorityCode: string;
      authorityId: number;
      authorityName?: string;
      passwordSetNow?: boolean;
      accessToken: string;
      tokenType?: string;
      expiresIn?: string;
    }>
  > {
    return this.http.post<
      ApiOne<{
        id: number;
        userId: string;
        userCode: string;
        displayName: string;
        role: string;
        authorityCode: string;
        authorityId: number;
        authorityName?: string;
        passwordSetNow?: boolean;
        accessToken: string;
        tokenType?: string;
        expiresIn?: string;
      }>
    >(`${API_BASE}/auth/login`, body);
  }

  me(): Observable<
    ApiOne<{
      userId: string;
      displayName: string;
      role: string;
      authorityCode: string;
      authorityId: number;
      authMode?: string;
    }>
  > {
    return this.http.get<
      ApiOne<{
        userId: string;
        displayName: string;
        role: string;
        authorityCode: string;
        authorityId: number;
        authMode?: string;
      }>
    >(`${API_BASE}/auth/me`, this.opts());
  }

  // Masters — LARIS bye-law
  listByeLaws(params?: Record<string, string>): Observable<ApiList<ByeLawNorm>> {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.http.get<ApiList<ByeLawNorm>>(`${API_BASE}/bye-law-norms${q}`, this.opts());
  }

  createByeLaw(body: Record<string, unknown>): Observable<ApiOne<ByeLawNorm>> {
    return this.http.post<ApiOne<ByeLawNorm>>(`${API_BASE}/bye-law-norms`, body, this.opts());
  }

  updateByeLaw(id: number, body: Record<string, unknown>): Observable<ApiOne<ByeLawNorm>> {
    return this.http.put<ApiOne<ByeLawNorm>>(`${API_BASE}/bye-law-norms/${id}`, body, this.opts());
  }

  deleteByeLaw(id: number): Observable<ApiOne<{ id: number; statusCd: string }>> {
    return this.http.delete<ApiOne<{ id: number; statusCd: string }>>(
      `${API_BASE}/bye-law-norms/${id}`,
      this.opts()
    );
  }

  listHierarchyTypes(params?: Record<string, string>): Observable<ApiList<HierarchyType>> {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.http.get<ApiList<HierarchyType>>(`${API_BASE}/system-hierarchy-types${q}`, this.opts());
  }

  createHierarchyType(body: Record<string, unknown>): Observable<ApiOne<HierarchyType>> {
    return this.http.post<ApiOne<HierarchyType>>(`${API_BASE}/system-hierarchy-types`, body, this.opts());
  }

  updateHierarchyType(id: number, body: Record<string, unknown>): Observable<ApiOne<HierarchyType>> {
    return this.http.put<ApiOne<HierarchyType>>(
      `${API_BASE}/system-hierarchy-types/${id}`,
      body,
      this.opts()
    );
  }

  deleteHierarchyType(id: number): Observable<ApiOne<{ id: number; statusCd: string }>> {
    return this.http.delete<ApiOne<{ id: number; statusCd: string }>>(
      `${API_BASE}/system-hierarchy-types/${id}`,
      this.opts()
    );
  }

  listCodeValues(params?: Record<string, string>): Observable<ApiList<SystemCodeValue>> {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.http.get<ApiList<SystemCodeValue>>(`${API_BASE}/system-code-values${q}`, this.opts());
  }

  createCodeValue(body: Record<string, unknown>): Observable<ApiOne<SystemCodeValue>> {
    return this.http.post<ApiOne<SystemCodeValue>>(`${API_BASE}/system-code-values`, body, this.opts());
  }

  updateCodeValue(id: number, body: Record<string, unknown>): Observable<ApiOne<SystemCodeValue>> {
    return this.http.put<ApiOne<SystemCodeValue>>(
      `${API_BASE}/system-code-values/${id}`,
      body,
      this.opts()
    );
  }

  deleteCodeValue(id: number): Observable<ApiOne<{ id: number; statusCd: string }>> {
    return this.http.delete<ApiOne<{ id: number; statusCd: string }>>(
      `${API_BASE}/system-code-values/${id}`,
      this.opts()
    );
  }

  listPilotAreas(): Observable<ApiList<PilotArea>> {
    return this.http.get<ApiList<PilotArea>>(`${API_BASE}/pilot-areas`, this.opts());
  }

  createPilotArea(body: Record<string, unknown>): Observable<ApiOne<PilotArea>> {
    return this.http.post<ApiOne<PilotArea>>(`${API_BASE}/pilot-areas`, body, this.opts());
  }

  // LARIS location hierarchy (MariaDB — Mam spec)
  listLocationTypes(params?: Record<string, string>): Observable<ApiList<LocationType>> {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.http.get<ApiList<LocationType>>(`${API_BASE}/location-types${q}`, this.opts());
  }

  createLocationType(body: {
    code: string;
    name: string;
    fields: LocationTypeField[];
    scope?: 'global' | 'authority';
  }): Observable<ApiOne<LocationType>> {
    return this.http.post<ApiOne<LocationType>>(`${API_BASE}/location-types`, body, this.opts());
  }

  updateLocationType(
    id: number,
    body: Partial<Pick<LocationType, 'name' | 'fields' | 'isActive' | 'statusCd'>>
  ): Observable<ApiOne<LocationType>> {
    return this.http.put<ApiOne<LocationType>>(`${API_BASE}/location-types/${id}`, body, this.opts());
  }

  deleteLocationType(id: number): Observable<ApiOne<{ id: number; statusCd: string }>> {
    return this.http.delete<ApiOne<{ id: number; statusCd: string }>>(
      `${API_BASE}/location-types/${id}`,
      this.opts()
    );
  }

  listSystemLocations(params?: Record<string, string>): Observable<ApiList<SystemLocation>> {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.http.get<ApiList<SystemLocation>>(`${API_BASE}/system-locations${q}`, this.opts());
  }

  getSystemLocation(id: number): Observable<ApiOne<SystemLocation>> {
    return this.http.get<ApiOne<SystemLocation>>(`${API_BASE}/system-locations/${id}`, this.opts());
  }

  createSystemLocation(body: {
    locationTypeId: number;
    parentLocationId?: number | null;
    code: string;
    name: string;
    attributes?: Record<string, unknown>;
    boundaryGeojson?: unknown | null;
  }): Observable<ApiOne<SystemLocation>> {
    return this.http.post<ApiOne<SystemLocation>>(`${API_BASE}/system-locations`, body, this.opts());
  }

  updateSystemLocation(
    id: number,
    body: Partial<{
      code: string;
      name: string;
      attributes: Record<string, unknown>;
      boundaryGeojson: unknown | null;
      statusCd: string;
      isActive: boolean;
    }>
  ): Observable<ApiOne<SystemLocation>> {
    return this.http.put<ApiOne<SystemLocation>>(`${API_BASE}/system-locations/${id}`, body, this.opts());
  }

  bulkImportPlotBoundaries(body: {
    sectorId: number;
    geojson: Record<string, unknown>;
    dryRun?: boolean;
  }): Observable<
    ApiOne<BulkPlotBoundaryImportResult> & {
      meta?: {
        dryRun?: boolean;
        savedCount?: number;
        featureCount?: number;
        plotCount?: number;
        unmatchedFeatureCount?: number;
        unmatchedPlotCount?: number;
      };
    }
  > {
    return this.http.post<ApiOne<BulkPlotBoundaryImportResult>>(
      `${API_BASE}/system-locations/bulk-boundaries`,
      body,
      this.opts()
    );
  }

  deleteSystemLocation(id: number): Observable<ApiOne<{ id: number; statusCd: string }>> {
    return this.http.delete<ApiOne<{ id: number; statusCd: string }>>(
      `${API_BASE}/system-locations/${id}`,
      this.opts()
    );
  }

  listLocationLidarScans(locationId: number): Observable<ApiList<LidarScan>> {
    return this.http.get<ApiList<LidarScan>>(
      `${API_BASE}/system-locations/${locationId}/lidar-scans`,
      this.opts()
    );
  }

  attachLidarScan(
    locationId: number,
    body: {
      scanDate: string;
      pointCloudFormat?: string;
      status?: string;
      documentId?: number | null;
      pointCount?: number | null;
      attributes?: Record<string, unknown>;
    }
  ): Observable<ApiOne<LidarScan>> {
    return this.http.post<ApiOne<LidarScan>>(
      `${API_BASE}/system-locations/${locationId}/lidar-scans`,
      body,
      this.opts()
    );
  }

  listLocationScanJobs(locationId: number): Observable<ApiList<ScanJob>> {
    return this.http.get<ApiList<ScanJob>>(
      `${API_BASE}/system-locations/${locationId}/scan-jobs`,
      this.opts()
    );
  }

  uploadLocationScanJob(
    locationId: number,
    form: FormData
  ): Observable<ApiOne<ScanJob> & { meta?: Record<string, unknown> }> {
    return this.http.post<ApiOne<ScanJob> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/system-locations/${locationId}/scan-jobs/upload`,
      form,
      this.uploadOpts()
    );
  }

  deleteLocationScanJob(locationId: number, scanJobId: number): Observable<ApiOne<Record<string, unknown>>> {
    return this.http.delete<ApiOne<Record<string, unknown>>>(
      `${API_BASE}/system-locations/${locationId}/scan-jobs/${scanJobId}`,
      this.opts()
    );
  }

  processLocationScans(
    locationId: number,
    body: { inputScanJobIds: number[] }
  ): Observable<ApiOne<Record<string, unknown>> & { meta?: Record<string, unknown> }> {
    return this.http.post<ApiOne<Record<string, unknown>> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/system-locations/${locationId}/scan-processing-runs`,
      body,
      this.opts()
    );
  }

  listLocationScanProcessingRuns(
    locationId: number,
    limit = 5
  ): Observable<ApiList<LocationScanProcessingRun>> {
    return this.http.get<ApiList<LocationScanProcessingRun>>(
      `${API_BASE}/system-locations/${locationId}/scan-processing-runs`,
      { ...this.opts(), params: { limit: String(limit) } }
    );
  }

  ingestLocationDemoLidar(
    locationId: number,
    body: { autoProcess?: boolean } = {}
  ): Observable<ApiList<ScanJob> & { meta?: Record<string, unknown> }> {
    return this.http.post<ApiList<ScanJob> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/system-locations/${locationId}/scan-jobs/ingest-demo`,
      body,
      this.opts()
    );
  }

  // PostGIS spatial hierarchy (legacy — flexible node types)
  listSpatialNodeTypes(): Observable<ApiList<SpatialNodeType>> {
    return this.http.get<ApiList<SpatialNodeType>>(`${API_BASE}/spatial-node-types`, this.opts());
  }

  createSpatialNodeType(typeName: string): Observable<ApiOne<SpatialNodeType>> {
    return this.http.post<ApiOne<SpatialNodeType>>(
      `${API_BASE}/spatial-node-types`,
      { typeName },
      this.opts()
    );
  }

  listSpatialNodes(params?: Record<string, string>): Observable<ApiList<SpatialNode>> {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.http.get<ApiList<SpatialNode>>(`${API_BASE}/spatial-nodes${q}`, this.opts());
  }

  getSpatialNode(id: number): Observable<ApiOne<SpatialNode>> {
    return this.http.get<ApiOne<SpatialNode>>(`${API_BASE}/spatial-nodes/${id}`, this.opts());
  }

  createSpatialNode(body: {
    parentId: number;
    code: string;
    name: string;
    nodeType: string;
    boundaryGeojson?: unknown | null;
    isPlotRegistry?: boolean;
  }): Observable<ApiOne<SpatialNode>> {
    return this.http.post<ApiOne<SpatialNode>>(`${API_BASE}/spatial-nodes`, body, this.opts());
  }

  updateSpatialNode(
    id: number,
    body: {
      code?: string;
      name?: string;
      nodeType?: string;
      statusCd?: string;
      boundaryGeojson?: unknown | null;
      isPlotRegistry?: boolean;
    }
  ): Observable<ApiOne<SpatialNode>> {
    return this.http.put<ApiOne<SpatialNode>>(`${API_BASE}/spatial-nodes/${id}`, body, this.opts());
  }

  deleteSpatialNode(id: number): Observable<ApiOne<{ id: number; code: string; nodeType: string; statusCd: string }>> {
    return this.http.delete<ApiOne<{ id: number; code: string; nodeType: string; statusCd: string }>>(
      `${API_BASE}/spatial-nodes/${id}`,
      this.opts()
    );
  }

  getSpatialNodeBoundary(id: number): Observable<ApiOne<SpatialBoundary>> {
    return this.http.get<ApiOne<SpatialBoundary>>(
      `${API_BASE}/spatial-nodes/${id}/boundary`,
      this.opts()
    );
  }

  getPlotBoundary(plotId: string): Observable<ApiOne<PlotBoundary>> {
    return this.http.get<ApiOne<PlotBoundary>>(
      `${API_BASE}/plot-boundaries/${encodeURIComponent(plotId)}`,
      this.opts()
    );
  }

  listScanBatches(params?: Record<string, string>): Observable<ApiList<ScanBatch>> {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.http.get<ApiList<ScanBatch>>(`${API_BASE}/scan-batches${q}`, this.opts());
  }

  createScanBatch(body: {
    spatialNodeId?: number;
    systemLocationId?: number;
    batchLabel?: string;
    expectedFileCount?: number;
    notes?: string | null;
    metadataJson?: Record<string, unknown>;
  }): Observable<ApiOne<ScanBatch>> {
    return this.http.post<ApiOne<ScanBatch>>(`${API_BASE}/scan-batches`, body, this.opts());
  }

  updateScanBatch(
    id: number,
    body: Partial<Pick<ScanBatch, 'batchLabel' | 'expectedFileCount' | 'notes' | 'statusCd'>>
  ): Observable<ApiOne<ScanBatch>> {
    return this.http.patch<ApiOne<ScanBatch>>(`${API_BASE}/scan-batches/${id}`, body, this.opts());
  }

  // Plans
  listPlans(): Observable<ApiList<SanctionedPlan>> {
    return this.http.get<ApiList<SanctionedPlan>>(`${API_BASE}/sanctioned-plans`, this.opts());
  }

  createPlan(body: Record<string, unknown>): Observable<ApiOne<SanctionedPlan>> {
    return this.http.post<ApiOne<SanctionedPlan>>(`${API_BASE}/sanctioned-plans`, body, this.opts());
  }

  /** Register a plan using a built-in sample PDF, or download from a public URL. */
  ingestSamplePlan(body: Record<string, unknown>): Observable<ApiOne<SanctionedPlan> & { meta?: Record<string, unknown> }> {
    return this.http.post<ApiOne<SanctionedPlan> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/sanctioned-plans/ingest-sample`,
      body,
      this.opts()
    );
  }

  uploadPlan(form: FormData): Observable<ApiOne<SanctionedPlan> & { meta?: Record<string, unknown> }> {
    return this.http.post<ApiOne<SanctionedPlan> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/sanctioned-plans/upload`,
      form,
      this.uploadOpts()
    );
  }

  getPlanDeleteImpact(id: number): Observable<ApiOne<DeleteImpact>> {
    return this.http.get<ApiOne<DeleteImpact>>(
      `${API_BASE}/sanctioned-plans/${id}/delete-impact`,
      this.opts()
    );
  }

  deletePlan(id: number): Observable<ApiOne<Record<string, unknown>> & { meta?: Record<string, unknown> }> {
    return this.http.delete<ApiOne<Record<string, unknown>> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/sanctioned-plans/${id}`,
      this.opts()
    );
  }

  digitizePlan(id: number): Observable<ApiOne<SanctionedPlan> & { meta?: Record<string, unknown> }> {
    return this.http.post<ApiOne<SanctionedPlan> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/sanctioned-plans/${id}/digitize`,
      {},
      this.opts()
    );
  }

  /** Upload LiDAR image/LAS and auto-compare with plan OCR data */
  compareLidarToPlan(
    planId: number,
    file: File
  ): Observable<ApiOne<SanctionedPlan> & { meta?: Record<string, unknown> }> {
    const fd = new FormData();
    fd.append('file', file);
    return this.http.post<ApiOne<SanctionedPlan> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/sanctioned-plans/${planId}/lidar-compare`,
      fd,
      this.uploadOpts()
    );
  }

  verifyPlan(id: number, body: { decision: string; comment?: string }): Observable<ApiOne<SanctionedPlan>> {
    return this.http.post<ApiOne<SanctionedPlan>>(
      `${API_BASE}/sanctioned-plans/${id}/verify`,
      body,
      this.opts()
    );
  }

  // Scans
  listScanJobs(): Observable<ApiList<ScanJob>> {
    return this.http.get<ApiList<ScanJob>>(`${API_BASE}/scan-jobs`, this.opts());
  }

  getScanDeleteImpact(id: number): Observable<ApiOne<DeleteImpact>> {
    return this.http.get<ApiOne<DeleteImpact>>(
      `${API_BASE}/scan-jobs/${id}/delete-impact`,
      this.opts()
    );
  }

  deleteScanJob(id: number): Observable<ApiOne<Record<string, unknown>> & { meta?: Record<string, unknown> }> {
    return this.http.delete<ApiOne<Record<string, unknown>> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/scan-jobs/${id}`,
      this.opts()
    );
  }

  createScanJob(body: Record<string, unknown>): Observable<ApiOne<ScanJob>> {
    return this.http.post<ApiOne<ScanJob>>(`${API_BASE}/scan-jobs`, body, this.opts());
  }

  uploadScanJob(form: FormData): Observable<ApiOne<ScanJob> & { meta?: Record<string, unknown> }> {
    return this.http.post<ApiOne<ScanJob> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/scan-jobs/upload`,
      form,
      this.uploadOpts()
    );
  }

  ingestDemoLidar(body: {
    pilotAreaId?: number;
    autoProcess?: boolean;
    plotRef?: string;
  }): Observable<ApiList<ScanJob> & { meta?: Record<string, unknown> }> {
    return this.http.post<ApiList<ScanJob> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/scan-jobs/ingest-demo`,
      body,
      this.opts()
    );
  }

  startProcessing(body: {
    pilotAreaId: number;
    inputScanJobIds: number[];
    plotRef: string;
  }): Observable<ApiOne<Record<string, unknown>> & { meta?: Record<string, unknown> }> {
    return this.http.post<ApiOne<Record<string, unknown>> & { meta?: Record<string, unknown> }>(
      `${API_BASE}/scan-processing-runs`,
      body,
      this.opts()
    );
  }

  listProcessingRuns(): Observable<ApiList<Record<string, unknown>>> {
    return this.http.get<ApiList<Record<string, unknown>>>(
      `${API_BASE}/scan-processing-runs`,
      this.opts()
    );
  }

  listAuditEvents(options?: {
    entityType?: string;
    page?: number;
    pageSize?: number;
  }): Observable<
    ApiList<Record<string, unknown>> & {
      meta?: {
        total?: number;
        page?: number;
        pageSize?: number;
        totalPages?: number;
        count?: number;
      };
    }
  > {
    type AuditList = ApiList<Record<string, unknown>> & {
      meta?: {
        total?: number;
        page?: number;
        pageSize?: number;
        totalPages?: number;
        count?: number;
      };
    };
    const params = new URLSearchParams();
    if (options?.entityType) params.set('entityType', options.entityType);
    if (options?.page != null) params.set('page', String(options.page));
    if (options?.pageSize != null) params.set('pageSize', String(options.pageSize));
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.http.get<AuditList>(`${API_BASE}/audit-events${q}`, this.opts());
  }

  // Measurements + rule check
  listMeasurements(options?: { statusCd?: string }): Observable<ApiList<Measurement>> {
    const params = new URLSearchParams();
    if (options?.statusCd) params.set('statusCd', options.statusCd);
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.http.get<ApiList<Measurement>>(`${API_BASE}/measurements${q}`, this.opts());
  }

  compareMeasurement(plotRef: string): Observable<
    ApiOne<{
      plotRef: string;
      measurement: Measurement;
      plan: Record<string, unknown> | null;
      compare: Record<string, unknown>;
      byeLaw?: { plotZone?: string; rules?: Array<Record<string, unknown>> };
      unifiedRows?: Array<{
        field: string;
        unit?: string;
        measured: number | string | null;
        plan: number | string | null;
        planStatus?: string;
        planTolerance?: number | string | null;
        byeLawLimit: number | string | null;
        byeLawStatus?: string;
        byeLawTolerance?: number | string | null;
      }>;
    }>
  > {
    return this.http.get<
      ApiOne<{
        plotRef: string;
        measurement: Measurement;
        plan: Record<string, unknown> | null;
        compare: Record<string, unknown>;
        byeLaw?: { plotZone?: string; rules?: Array<Record<string, unknown>> };
        unifiedRows?: Array<{
          field: string;
          unit?: string;
          measured: number | string | null;
          plan: number | string | null;
          planStatus?: string;
          planTolerance?: number | string | null;
          byeLawLimit: number | string | null;
          byeLawStatus?: string;
          byeLawTolerance?: number | string | null;
        }>;
      }>
    >(`${API_BASE}/measurements/compare?plotRef=${encodeURIComponent(plotRef)}`, this.opts());
  }

  createMeasurement(body: Record<string, unknown>): Observable<ApiOne<Measurement>> {
    return this.http.post<ApiOne<Measurement>>(`${API_BASE}/measurements`, body, this.opts());
  }

  startRuleCheck(body: {
    locationId?: number;
    pilotAreaId?: number;
    runLabel?: string;
    forceRecheck?: boolean;
  }): Observable<ApiOne<unknown>> {
    return this.http.post<ApiOne<unknown>>(`${API_BASE}/rule-check-runs`, body, this.opts());
  }

  listRuleCheckRuns(limit = 50): Observable<ApiList<RuleCheckRun>> {
    return this.http.get<ApiList<RuleCheckRun>>(
      `${API_BASE}/rule-check-runs?limit=${limit}`,
      this.opts()
    );
  }

  purgeSupersededHistory(): Observable<
    ApiOne<{ measurementsDeleted: number; flagsDeleted: number }>
  > {
    return this.http.post<ApiOne<{ measurementsDeleted: number; flagsDeleted: number }>>(
      `${API_BASE}/maintenance/purge-superseded`,
      {},
      this.opts()
    );
  }

  // Flags
  listFlags(statusCd?: string): Observable<ApiList<ViolationFlag>> {
    const q = statusCd ? `?statusCd=${encodeURIComponent(statusCd)}` : '';
    return this.http.get<ApiList<ViolationFlag>>(`${API_BASE}/violation-flags${q}`, this.opts());
  }

  getFlag(id: number): Observable<ApiOne<FlagDetail>> {
    return this.http.get<ApiOne<FlagDetail>>(`${API_BASE}/violation-flags/${id}`, this.opts());
  }

  reviewFlag(
    id: number,
    body: { decision: 'accept' | 'reject' | 'recheck'; comment?: string | null }
  ): Observable<ApiOne<unknown>> {
    return this.http.post<ApiOne<unknown>>(
      `${API_BASE}/violation-flags/${id}/reviews`,
      body,
      this.opts()
    );
  }
}
